import { coreApi, BlueprintFamilyTrendsWindowDays } from '../core'

// Bucket keys of the duration-distribution histogram, ascending. `D32_PLUS` is the
// open-ended tail (no `maxDays`). `D1` is exactly one day.
export type BlueprintFamilyDurationBucketLabel = 'D1' | 'D2_3' | 'D4_6' | 'D7_13' | 'D14_27' | 'D28_31' | 'D32_PLUS'

// One bucket of POST /blueprint-families/{id}/duration-distribution (GR-2916).
// Mirrors BE BlueprintFamilyDurationDistributionRespDto. The BE always returns all 7
// buckets in ascending order (zero-filled where empty, including for an id with no events
// at all). Spans are inclusive calendar days, matching the FE's `eventDurationDays()`
// (`differenceInCalendarDays + 1`); `maxDays` is the inclusive upper bound and is omitted
// entirely on the unbounded `D32_PLUS` tail.
//
// IMPORTANT: a game counts once in EVERY bucket it ran an event in, not once in the bucket
// implied by its median duration. 35.6% of (family, game) cells span more than one bucket
// on prod, so bucket totals no longer sum to the family's game count — these are not a
// partition. Do not render them as "x of N games". Per-event counting was considered and
// rejected: one game with 1,638 events would have swamped the other 85.
export type BlueprintFamilyDurationDistributionApiItem = {
  label: BlueprintFamilyDurationBucketLabel
  // Absent on the unbounded `D32_PLUS` tail.
  maxDays?: number
  gamesCount: number
}

type GetBlueprintFamilyDurationDistributionQueryParams = {
  blueprintFamilyId: string
  windowDays?: BlueprintFamilyTrendsWindowDays
  includePcConsole?: boolean
  // Scopes the histogram to these subgenres. Empty/absent = whole catalogue.
  conventionalSubgenreIds?: string[]
}

const durationDistributionApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    // POST so segment scoping can be sent as a `conventionalSubgenreIds` body list
    // (a body is required — `{ conventionalSubgenreIds: [] }` = whole catalogue).
    getBlueprintFamilyDurationDistribution: builder.query<BlueprintFamilyDurationDistributionApiItem[], GetBlueprintFamilyDurationDistributionQueryParams>({
      query: ({ blueprintFamilyId, windowDays, includePcConsole, conventionalSubgenreIds }) => ({
        url: `blueprint-families/${blueprintFamilyId}/duration-distribution`,
        method: 'POST',
        params: {
          ...(includePcConsole !== undefined ? { includePcConsole } : {}),
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: { conventionalSubgenreIds: conventionalSubgenreIds ?? [] },
      }),
      providesTags: (_result, _err, { blueprintFamilyId, windowDays, conventionalSubgenreIds }) => [
        {
          type: 'BlueprintFamilies',
          id: `${blueprintFamilyId}/duration-distribution/${windowDays ?? 'default'}/${[...(conventionalSubgenreIds ?? [])].sort().join(',') || 'all'}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyDurationDistributionQuery } = durationDistributionApi
